import {
  AccountDetailsService,
  ApiTokensService,
  OAuthClientsService,
  ProjectsService,
  AnalyticsService,
  UsersService,
  EventsService,
  DashboardService,
  DailyActiveUsersService,
  ReportsService,
} from '@/api';
import { CompanyActivityService } from '@/api/company-activity-service';
import { GroupsService } from '@/api/groups-service';
import { RevenueService } from '@/api/revenue-service';
import {
  PaymentService,
  type AdminSubscription,
  type StripeCoupon,
} from '@/api/payments-service';
import {
  PrivacyRequestService,
  type PrivacyRequest,
} from '@/api/privacy-requests-service';
import { FeaturesService } from '@/api/features-service';
import { McpConnectionsService } from '@/api/mcp-connections-service';
import { OnboardingService } from '@/api/onboarding-service';
import { PromoService } from '@/api/promo-service';
import { EnrichmentService } from '@/api/enrichment-service';
import {
  BillingCreditsService,
  type ActionUsage,
  type CreditWallet,
} from '@/api/billing-credits-service';
import { AiService, type AiSettings } from '@/api/ai-service';
import {
  CompanyProfilesService,
  type CompanyProfile,
} from '@/api/company-profiles-service';
import {
  DataManagementService,
  type ProposedDataChange,
  type LookupValue,
} from '@/api/data-management-service';
import { ActionsService } from '@/api/actions-service';
import { SignalsService, type SignalsListParams } from '@/api/signals-service';
import {
  AccountWatchService,
  type AccountWatchEstimate,
} from '@/api/account-watch-service';
import {
  ActionInvocationsService,
  type ActionEstimate,
} from '@/api/action-invocations-service';
import { ActionRunsService } from '@/api/action-runs-service';
import {
  AdminProfileService,
  CustomerProfileService,
  type ProfileRange,
} from '@/api/profile-service';
import type { SuperAdminUser } from '@/components/super-admin/columns';
import { authClient } from '@/lib/auth-client';
import type {
  DateRange,
  GetRetention,
  GetUsers,
  GetInfiniteEvents,
  GetAllEventsCount,
  UserEvents,
  FilterGroup,
} from '@/types';
import {
  queryOptions,
  infiniteQueryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import type { CompanyProfilesListResponse } from '@logspot-platform/platform-types';
import { endOfDay, endOfHour, sub, subDays } from 'date-fns';

export const DATE_RANGE = 30;

/** Stale times — keep consistent across queries of the same category */
const STALE_TIME_DATA = 1000 * 60; // 1 minute — event data, analytics, usage
const STALE_TIME_CONFIG = 1000 * 60 * 60; // 1 hour — projects, automations

// GDPR/CCPA data-subject requests (the active org's self-service list). Polls
// while any request is still in-flight, then stops once all are terminal.
export const privacyRequestsOptions = () =>
  queryOptions({
    queryKey: ['privacy-requests'] as const,
    queryFn: (): Promise<PrivacyRequest[]> => PrivacyRequestService.list(),
    staleTime: 0,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (r) => r.status === 'pending' || r.status === 'processing',
      )
        ? 3000
        : false,
  });

// Cross-org oversight list for platform/super-admins (read-only). Same polling
// cadence so in-flight requests across every org stay current.
export const allPrivacyRequestsOptions = () =>
  queryOptions({
    queryKey: ['privacy-requests', 'all'] as const,
    queryFn: (): Promise<PrivacyRequest[]> =>
      PrivacyRequestService.listAllRequests(),
    staleTime: 0,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (r) => r.status === 'pending' || r.status === 'processing',
      )
        ? 3000
        : false,
  });

export const signUpOffersOptions = () =>
  queryOptions({
    queryKey: ['promos', 'offers'],
    queryFn: () => PromoService.offers(),
    staleTime: STALE_TIME_DATA,
  });

// Owner-only on the server; callers enable it for paid plans.
export const billingCurrentOptions = () =>
  queryOptions({
    queryKey: ['billing-current'],
    queryFn: () => PaymentService.getCurrentBilling(),
  });

export const accountDetailsOptions = () =>
  queryOptions({
    queryKey: ['account-details'],
    queryFn: async () => await AccountDetailsService.getAccountDetails(),
    staleTime: STALE_TIME_DATA,
    retry: 1,
  });

// A plan change (checkout return, lifetime deal claim) touches plan, billing and credits at once.
export const invalidatePlanQueries = (queryClient: QueryClient) =>
  Promise.all(
    [
      'account-details',
      'billing-current',
      'billing-credits',
      'billing-action-usage',
    ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
  );

// Organization credentials (Integrations → AI & API Access), shared so every creator can invalidate them.
export const apiTokensOptions = () =>
  queryOptions({
    queryKey: ['api-tokens'] as const,
    queryFn: () => ApiTokensService.list(),
    retry: false,
  });

export const oauthClientsOptions = () =>
  queryOptions({
    queryKey: ['oauth-clients'] as const,
    queryFn: () => OAuthClientsService.list(),
    retry: false,
  });

// Why company identification is or isn't running for a project (off / active / paused).
export const visitorIdentificationStatusOptions = (projectId: string) =>
  queryOptions({
    queryKey: ['visitor-identification-status', projectId] as const,
    queryFn: () => EnrichmentService.getVisitorIdentificationStatus(projectId),
    staleTime: 60_000,
  });

// Lean onboarding + Launch Checklist state for the active org (tasks detected on read).
export const onboardingOptions = () =>
  queryOptions({
    queryKey: ['onboarding'] as const,
    queryFn: () => OnboardingService.get(),
  });

// The user's OAuth grants to MCP clients (My Profile > Connected Applications).
export const mcpConnectionsOptions = () =>
  queryOptions({
    queryKey: ['mcp-connections'] as const,
    queryFn: () => McpConnectionsService.list(),
    staleTime: 0,
  });

export const infiniteEventsOptions = ({
  projectId,
  filterGroups,
  selectedEvent,
  dateRange,
}: GetInfiniteEvents) =>
  infiniteQueryOptions({
    queryKey: [
      'events_search',
      { projectId, selectedEvent, filterGroups, dateRange },
    ],
    queryFn: async ({ pageParam }) => {
      return await EventsService.searchEvents({
        projectId,
        eventName: selectedEvent,
        filterGroups: filterGroups,
        cursor: pageParam ? pageParam : undefined,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
    },
    getNextPageParam: (lastPage) => lastPage?.cursor,
    refetchInterval: 60000,
    initialPageParam: null as string | null,
    staleTime: STALE_TIME_DATA,
  });

export const projectsOptions = () =>
  queryOptions({
    queryKey: ['projects'],
    queryFn: async () => await ProjectsService.getProjects(),
    staleTime: STALE_TIME_CONFIG,
  });

// Better Auth's authClient.getSession() is a raw fetch with no client-side
// dedup. We wrap it in queryOptions so route loaders (ensureQueryData) and
// any imperative reads share TanStack Query's cache — without this, every
// route navigation re-fires GET /api/auth/get-session. useSession() in
// components stays on Better Auth's nanostore atom (reactive); this cache is
// just for loader-side reads. setActiveOrganization keeps the two in sync by
// writing the fresh session into this cache after a server-side org switch.
export const sessionOptions = () =>
  queryOptions({
    queryKey: ['auth', 'session'] as const,
    queryFn: async () => {
      const { data, error } = await authClient.getSession();
      if (error) {
        throw new Error(error.message || 'Failed to load session');
      }
      return data;
    },
    staleTime: STALE_TIME_CONFIG,
  });

export const organizationsOptions = ({ userId }: { userId: string }) =>
  queryOptions({
    queryKey: ['auth', 'organizations', userId],
    queryFn: async () => {
      const { data, error } = await authClient.organization.list();
      if (error) {
        throw new Error(error.message || 'Failed to load organizations');
      }
      return data ?? [];
    },
    staleTime: STALE_TIME_CONFIG,
  });

export const activeMemberRoleOptions = ({
  userId,
  activeOrganizationId,
}: {
  userId: string;
  activeOrganizationId: string;
}) =>
  queryOptions({
    queryKey: ['auth', 'active-member-role', userId, activeOrganizationId],
    queryFn: async () => {
      const { data, error } =
        await authClient.organization.getActiveMemberRole();
      if (error) {
        throw new Error(error.message || 'Failed to load active member role');
      }
      return data.role;
    },
    staleTime: STALE_TIME_CONFIG,
  });

export const retentionOptions = ({
  projectId,
  days,
  filterGroups,
}: GetRetention) =>
  queryOptions({
    queryKey: ['retention', projectId, days, filterGroups],
    queryFn: async () =>
      await AnalyticsService.getRetention({
        projectId,
        startDate: sub(new Date(), { days: days }),
        filterGroups,
      }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });

export const allEventsOptions = ({
  projectId,
  dateRange,
  filterGroups,
}: GetAllEventsCount) =>
  queryOptions({
    queryKey: ['events-all:count', projectId, dateRange, filterGroups],
    queryFn: () =>
      EventsService.getAllEventCounts({
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate.getTime(),
        endDate: dateRange.endDate.getTime(),
        filterGroups,
        userId: null,
      }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });

// The window just before `dateRange`, which every period comparison uses.
export const previousDateRange = (dateRange: DateRange): DateRange => ({
  interval: dateRange.interval,
  startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
  endDate: dateRange.interval.includes('days')
    ? endOfDay(dateRange.startDate)
    : endOfHour(dateRange.startDate),
});

export const allEventsPrevOptions = ({
  projectId,
  dateRange,
  filterGroups,
}: GetAllEventsCount) => {
  const prevDateRange = previousDateRange(dateRange);

  return queryOptions({
    queryKey: ['events-all:count:previous', projectId, dateRange, filterGroups],
    queryFn: () =>
      EventsService.getAllEventCounts({
        projectId,
        interval: prevDateRange.interval,
        startDate: prevDateRange.startDate.getTime(),
        endDate: prevDateRange.endDate.getTime(),
        filterGroups,
        userId: null,
      }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });
};

export const aggregationByPropertyOptions = ({
  projectId,
  dateRange,
  eventName,
  property,
  sumProperty,
  filterGroups,
}: {
  projectId: string;
  dateRange: { startDate?: Date; endDate?: Date };
  eventName?: string;
  property?: string;
  sumProperty?: string;
  filterGroups?: FilterGroup[];
}) => {
  const isField = property?.[0] === '_';
  const searchField = isField ? property?.slice(1) : property;
  return queryOptions({
    queryKey: [
      'aggregation_per_property',
      projectId,
      dateRange,
      eventName,
      property,
      filterGroups,
      ...(sumProperty ? [{ sumProperty }] : []),
    ],
    queryFn: () =>
      AnalyticsService.getAggregationByProperty({
        projectId,
        eventName,
        ...(isField && { field: searchField }),
        ...(!isField && { metadataField: property }),
        ...(sumProperty && { sumMetadataField: sumProperty }),
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        filterGroups,
      }),
  });
};

export const dashboardConfigOptions = ({ projectId }: { projectId: string }) =>
  queryOptions({
    queryKey: ['dashboard-config', projectId],
    queryFn: () => DashboardService.getDashboardConfig({ projectId }),
    staleTime: STALE_TIME_CONFIG,
  });

export const dashboardUserLayoutOptions = ({
  projectId,
}: {
  projectId: string;
}) =>
  queryOptions({
    queryKey: ['dashboard-layout-me', projectId],
    queryFn: () => DashboardService.getUserLayout({ projectId }),
    staleTime: STALE_TIME_CONFIG,
  });

export const usersOptions = ({ projectId, page }: GetUsers) =>
  queryOptions({
    queryKey: ['users', projectId, page],
    queryFn: async () => await UsersService.getUsers({ projectId, page }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });

export const userEventsOptions = ({ projectId, userId }: UserEvents) =>
  queryOptions({
    queryKey: ['user:events-all', projectId, DATE_RANGE, userId],
    queryFn: () =>
      EventsService.getAllEventCounts({
        projectId,
        startDate: subDays(new Date(), DATE_RANGE).getTime(),
        endDate: new Date().getTime(),
        interval: '1 days',
        userId,
      }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });

export const reportsOptions = ({ projectId }: { projectId: string }) =>
  queryOptions({
    queryKey: ['reports', projectId],
    queryFn: ({ signal }) => ReportsService.getReports({ projectId, signal }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });

export const eventNamesOptions = ({ projectId }: { projectId: string }) =>
  queryOptions({
    queryKey: ['event-names', projectId],
    queryFn: async () => EventsService.getEventNames({ projectId }),
    staleTime: STALE_TIME_CONFIG,
  });

export const eventPropertiesOptions = ({
  projectId,
  eventNames = [],
}: {
  projectId: string;
  eventNames?: readonly string[];
}) =>
  queryOptions({
    // Without names it shares the queries page's key for the project-wide set.
    queryKey:
      eventNames.length > 0
        ? ['event-properties', projectId, eventNames]
        : ['event-properties', projectId],
    queryFn: async () =>
      EventsService.getEventProperties({ projectId, eventNames }),
    staleTime: STALE_TIME_CONFIG,
  });

export const eventKeyValuesOptions = ({
  projectId,
  key,
}: {
  projectId: string;
  key: string;
}) =>
  queryOptions({
    queryKey: ['key-values', projectId, key],
    queryFn: async () => EventsService.getEventKeyValues({ projectId, key }),
    staleTime: STALE_TIME_CONFIG,
  });

// ── Super admin queries ──────────────────────────────────
//
// `placeholderData: keepPreviousData` is baked into paginated options so
// admins don't see a flash to a "Loading..." placeholder when paginating
// — the previous page's rows stay on screen until the new page resolves.
// Combine with `queryClient.prefetchQuery(...)` on Previous/Next hover for
// anticipatory loading.

type AdminUsersResult = { users: SuperAdminUser[]; total: number };

export const adminUsersOptions = ({
  offset,
  search,
  limit,
}: {
  offset: number;
  search: string;
  limit: number;
}) =>
  queryOptions({
    queryKey: ['admin-users', offset, search, limit] as const,
    queryFn: async (): Promise<AdminUsersResult> => {
      const query: Record<string, unknown> = {
        limit,
        offset,
        sortBy: 'createdAt',
        sortDirection: 'desc',
      };
      if (search) {
        query.searchValue = search;
        query.searchField = 'email';
      }
      const result = await authClient.admin.listUsers({ query } as never);
      const data = (result.data ?? {}) as Partial<AdminUsersResult>;
      return { users: data.users ?? [], total: data.total ?? 0 };
    },
    placeholderData: (previousData) => previousData,
    staleTime: STALE_TIME_DATA,
  });

export const adminSubscriptionsOptions = () =>
  queryOptions({
    queryKey: ['admin-subscriptions'] as const,
    queryFn: (): Promise<AdminSubscription[]> =>
      PaymentService.getAdminSubscriptions(),
    staleTime: STALE_TIME_DATA,
  });

export const adminStripeCouponsOptions = () =>
  queryOptions({
    queryKey: ['admin-stripe-coupons'] as const,
    queryFn: (): Promise<StripeCoupon[]> => PaymentService.getStripeCoupons(),
    staleTime: STALE_TIME_CONFIG,
  });

// ── Feature flags ────────────────────────────────────────
//
// `features` is the current org's effective flag map (any authed user) and
// gates launch surfaces, so it stays fresh (config stale time, refetch on
// mount). `adminFeatures*` back the super-admin Feature Flags page — the
// global-defaults view (no org) and the per-org resolved view.

export const featuresOptions = () =>
  queryOptions({
    queryKey: ['features'] as const,
    queryFn: () => FeaturesService.getFeatures(),
    staleTime: STALE_TIME_CONFIG,
  });

export const adminGlobalFeaturesOptions = () =>
  queryOptions({
    queryKey: ['admin-features', 'global'] as const,
    queryFn: () => FeaturesService.getAdminFeatures(),
    staleTime: STALE_TIME_DATA,
  });

export const adminOrgFeaturesOptions = (organizationId: string) =>
  queryOptions({
    queryKey: ['admin-features', 'org', organizationId] as const,
    queryFn: () => FeaturesService.getAdminFeatures(organizationId),
    staleTime: STALE_TIME_DATA,
  });

export const adminLaunchFlagsOptions = () =>
  queryOptions({
    queryKey: ['admin-features', 'launch'] as const,
    queryFn: () => FeaturesService.getLaunchFlags(),
    staleTime: STALE_TIME_DATA,
  });

export const adminAllOverridesOptions = () =>
  queryOptions({
    queryKey: ['admin-features', 'overrides'] as const,
    queryFn: () => FeaturesService.getAllFeatureOverrides(),
    staleTime: STALE_TIME_DATA,
  });

export const adminStrandedOverridesOptions = () =>
  queryOptions({
    queryKey: ['admin-features', 'stranded'] as const,
    queryFn: () => FeaturesService.getStrandedOverrides(),
    staleTime: STALE_TIME_DATA,
  });

// ── Data Credits + Actions usage + AI (launch-gated) ─────
//
// These back the flag-gated billing/AI surfaces. They're only mounted once the
// relevant flag resolves on (the caller guards with `useFeatures().isEnabled`),
// so the queries never fire — and never 403 — for orgs without the flag.

export const creditsWalletOptions = () =>
  queryOptions({
    queryKey: ['billing-credits'] as const,
    queryFn: (): Promise<CreditWallet> => BillingCreditsService.getCredits(),
    staleTime: STALE_TIME_DATA,
  });

export const actionUsageOptions = () =>
  queryOptions({
    queryKey: ['billing-action-usage'] as const,
    queryFn: (): Promise<ActionUsage> => BillingCreditsService.getUsage(),
    staleTime: STALE_TIME_DATA,
    retry: false,
  });

export const aiSettingsOptions = () =>
  queryOptions({
    queryKey: ['ai-settings'] as const,
    queryFn: (): Promise<AiSettings> => AiService.getSettings(),
    staleTime: STALE_TIME_CONFIG,
  });

// ── Identity profile queries ─────────────────────────────
//
// Profiles render on two surfaces backed by the same query shapes, so every
// factory takes a `ProfileScope` discriminated union and dispatches to the
// matching service (admin = identity-scoped; user = project+user-scoped, with
// the backend hiding identity resolution). The scope key keeps cache entries
// distinct between surfaces. `from`/`to` are ISO strings (omitted → backend's
// 30-day default). The activity calendar takes no range (fixed 365d).

export type ProfileScope =
  | { kind: 'identity'; identityId: string }
  | { kind: 'user'; projectId: string; userId: string };

const scopeKey = (scope: ProfileScope): string =>
  scope.kind === 'identity'
    ? `identity:${scope.identityId}`
    : `user:${scope.projectId}:${scope.userId}`;

export const profileMetricsOptions = (
  scope: ProfileScope,
  range: ProfileRange = {},
) =>
  queryOptions({
    queryKey: ['profile', 'metrics', scopeKey(scope), range.from, range.to],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getMetrics(scope.identityId, range)
        : CustomerProfileService.getMetrics(
            { projectId: scope.projectId, userId: scope.userId },
            range,
          ),
    staleTime: STALE_TIME_DATA,
  });

export const profileActivityOptions = (scope: ProfileScope) =>
  queryOptions({
    queryKey: ['profile', 'activity', scopeKey(scope)],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getActivity(scope.identityId)
        : CustomerProfileService.getActivity({
            projectId: scope.projectId,
            userId: scope.userId,
          }),
    staleTime: STALE_TIME_DATA,
  });

export const profileDeviceGeoOptions = (
  scope: ProfileScope,
  range: ProfileRange = {},
) =>
  queryOptions({
    queryKey: ['profile', 'device-geo', scopeKey(scope), range.from, range.to],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getDeviceGeo(scope.identityId, range)
        : CustomerProfileService.getDeviceGeo(
            { projectId: scope.projectId, userId: scope.userId },
            range,
          ),
    staleTime: STALE_TIME_DATA,
  });

export const profileTopPagesOptions = (
  scope: ProfileScope,
  range: ProfileRange & { limit?: number } = {},
) =>
  queryOptions({
    queryKey: ['profile', 'top-pages', scopeKey(scope), range.from, range.to],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getTopPages(scope.identityId, range)
        : CustomerProfileService.getTopPages(
            { projectId: scope.projectId, userId: scope.userId },
            range,
          ),
    staleTime: STALE_TIME_DATA,
  });

export const profileReferrersOptions = (
  scope: ProfileScope,
  range: ProfileRange & { limit?: number } = {},
) =>
  queryOptions({
    queryKey: ['profile', 'referrers', scopeKey(scope), range.from, range.to],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getReferrers(scope.identityId, range)
        : CustomerProfileService.getReferrers(
            { projectId: scope.projectId, userId: scope.userId },
            range,
          ),
    staleTime: STALE_TIME_DATA,
  });

export const profileTimeseriesOptions = (
  scope: ProfileScope,
  range: ProfileRange = {},
) =>
  queryOptions({
    queryKey: ['profile', 'timeseries', scopeKey(scope), range.from, range.to],
    queryFn: () =>
      scope.kind === 'identity'
        ? AdminProfileService.getTimeseries(scope.identityId, range)
        : CustomerProfileService.getTimeseries(
            { projectId: scope.projectId, userId: scope.userId },
            range,
          ),
    staleTime: STALE_TIME_DATA,
  });

export const profileSessionsOptions = (
  scope: ProfileScope,
  range: ProfileRange & { limit?: number } = {},
) =>
  infiniteQueryOptions({
    queryKey: ['profile', 'sessions', scopeKey(scope), range.from, range.to],
    queryFn: ({ pageParam }) =>
      scope.kind === 'identity'
        ? AdminProfileService.getSessions(scope.identityId, {
            ...range,
            cursor: pageParam,
          })
        : CustomerProfileService.getSessions(
            { projectId: scope.projectId, userId: scope.userId },
            { ...range, cursor: pageParam },
          ),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: null as string | null,
    staleTime: STALE_TIME_DATA,
  });

// Resolves whether a customer user_id maps to a stitched identity. Drives the
// "Identified" badge on the customer profile page; the profile modules
// themselves don't need this (the backend resolves transparently).
export const customerProfileIdentityOptions = ({
  projectId,
  userId,
}: {
  projectId: string;
  userId: string;
}) =>
  queryOptions({
    queryKey: ['profile', 'identity', `user:${projectId}:${userId}`],
    queryFn: () => CustomerProfileService.getIdentity({ projectId, userId }),
    staleTime: STALE_TIME_DATA,
  });

// The reusable AI agent catalog (static config) + the actions each powers.
// Read-only; long stale time since it changes only on deploy.
export const agentsOptions = () =>
  queryOptions({
    queryKey: ['agents'] as const,
    queryFn: () => AiService.listAgents(),
    staleTime: STALE_TIME_CONFIG,
  });

// The full action catalog (Browse Catalog). Returns the whole response
// (`{ actions, web_search_available }`) so the catalog can hide web-search-only
// actions when no provider is configured.
export const actionCatalogOptions = () =>
  queryOptions({
    queryKey: ['action-catalog'] as const,
    queryFn: () => AiService.listActionCatalog(),
    staleTime: STALE_TIME_CONFIG,
  });

// The org's actions (saved catalog-action instances; config only).
export const actionsOptions = () =>
  queryOptions({
    queryKey: ['actions'] as const,
    queryFn: () => ActionsService.list(),
    staleTime: STALE_TIME_CONFIG,
  });

// The action-run feed (Activity). Action data churns, so keep it fresh.
export const actionRunsOptions = () =>
  queryOptions({
    queryKey: ['action-runs'] as const,
    queryFn: () => ActionRunsService.list(),
    staleTime: STALE_TIME_DATA,
  });

export const LIVE_RUN_POLL_MS = 10_000;
export const LIVE_RUN_WINDOW_MS = 6 * 60 * 60 * 1000;

// A row still `running` past the window lost its closer, so polling it would never stop.
export const isLiveRun = (
  status: string | null,
  startedAt: string | null,
  now = Date.now(),
): boolean =>
  status === 'running' &&
  startedAt !== null &&
  now - new Date(startedAt).getTime() < LIVE_RUN_WINDOW_MS;

// A single action run's detail (per-run inspection).
export const actionRunOptions = (id: string) =>
  queryOptions({
    queryKey: ['action-runs', id] as const,
    queryFn: () => ActionRunsService.get(id),
    staleTime: STALE_TIME_DATA,
    refetchInterval: (query) =>
      query.state.data &&
      isLiveRun(query.state.data.status, query.state.data.started_at)
        ? LIVE_RUN_POLL_MS
        : false,
  });

// Pages through every run, since Activity's date filter and CSV export read the full history.
export const actionRunsFeedOptions = () =>
  infiniteQueryOptions({
    queryKey: ['action-runs', 'feed'] as const,
    queryFn: ({ pageParam }) => ActionRunsService.list({ cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    initialPageParam: null as string | null,
    staleTime: STALE_TIME_DATA,
    // A refetch re-requests every loaded page, so only a single-page feed polls.
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      return pages?.length === 1 &&
        pages[0]?.runs.some((run) => isLiveRun(run.status, run.started_at))
        ? LIVE_RUN_POLL_MS
        : false;
    },
  });

const hasLiveProfileRun = (profiles: CompanyProfile[]): boolean =>
  profiles.some((profile) => profile.live_run != null);

// A profile run that ended also wrote signals and billing, which these queries don't carry.
function refreshAfterProfileRuns(
  client: QueryClient,
  before: CompanyProfile[],
  after: CompanyProfile[],
) {
  const stillLive = new Set(after.map((profile) => profile.live_run?.id));
  if (before.some((p) => p.live_run && !stillLive.has(p.live_run.id))) {
    client.invalidateQueries({ queryKey: ['signals'] });
    client.invalidateQueries({ queryKey: ['billing-action-usage'] });
  }
}

// The org's Company Profiles (the GTM/ICP context the agents run on).
export const companyProfilesOptions = () =>
  queryOptions({
    queryKey: ['company-profiles'] as const,
    queryFn: async ({ client, queryKey }) => {
      const previous =
        client.getQueryData<CompanyProfilesListResponse>(queryKey);
      const list = await CompanyProfilesService.list();
      refreshAfterProfileRuns(client, previous?.profiles ?? [], list.profiles);
      return list;
    },
    staleTime: (query) =>
      hasLiveProfileRun(query.state.data?.profiles ?? [])
        ? 0
        : STALE_TIME_CONFIG,
    refetchInterval: (query) =>
      hasLiveProfileRun(query.state.data?.profiles ?? [])
        ? LIVE_RUN_POLL_MS
        : false,
  });

// One Company Profile (the /actions/profiles/$profileId detail page).
// Invalidating the ['company-profiles'] prefix refreshes this too.
export const companyProfileDetailOptions = (id: string) =>
  queryOptions({
    queryKey: ['company-profiles', id] as const,
    queryFn: async ({ client, queryKey }) => {
      const previous = client.getQueryData<CompanyProfile>(queryKey);
      const profile = await CompanyProfilesService.get(id);
      refreshAfterProfileRuns(client, previous ? [previous] : [], [profile]);
      return profile;
    },
    staleTime: (query) =>
      query.state.data && hasLiveProfileRun([query.state.data])
        ? 0
        : STALE_TIME_CONFIG,
    refetchInterval: (query) =>
      query.state.data && hasLiveProfileRun([query.state.data])
        ? LIVE_RUN_POLL_MS
        : false,
  });

// ── GTM Signals (launch-gated: `gtmSignals`) ─────────────────────────────
//
// The signals feed and per-account timeline are the SAME query shape (the
// timeline is the feed filtered by `profileId`). Cursor-paginated via the
// backend's opaque keyset `next_cursor`. Signal data churns as recipes fire, so
// it stays on the fresher data stale time.

// `filters` excludes the cursor (paging is handled by the infinite query) so the
// cache key is stable across pages. Reused for both the feed and the timeline.
export const signalsFeedOptions = (
  filters: Omit<SignalsListParams, 'cursor'> = {},
) =>
  infiniteQueryOptions({
    queryKey: ['signals', filters] as const,
    queryFn: ({ pageParam }) =>
      SignalsService.list({ ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    initialPageParam: null as string | null,
    staleTime: STALE_TIME_DATA,
  });

// The Account Watch spend preview (accounts × per-account weight ≈ weekly
// Actions). Only fetched when the Watch prompt / insights surfaces mount, so it
// never fires — or 403s — for orgs without `targetAccounts`.
export const accountWatchEstimateOptions = () =>
  queryOptions({
    queryKey: ['account-watch', 'estimate'] as const,
    queryFn: (): Promise<AccountWatchEstimate> =>
      AccountWatchService.estimate(),
    staleTime: STALE_TIME_DATA,
    retry: false,
  });

// The live credits-only per-run estimate for a single action (Actions + Data
// Credits {expected, max}). Keyed by action key + units so a per-unit estimate
// caches independently. Only mounted behind `actionsCredits` (the caller gates
// on `isEnabled('actionsCredits')`), so it never fires — or 403s — for orgs
// without the flag.
export const actionEstimateOptions = (key: string, units?: number) =>
  queryOptions({
    queryKey: ['action-estimate', key, units ?? null] as const,
    queryFn: (): Promise<ActionEstimate> =>
      ActionInvocationsService.estimate(key, units),
    staleTime: STALE_TIME_DATA,
    retry: false,
  });

// The maintained persona taxonomy (global seed + org-custom).
export const personasListOptions = () =>
  queryOptions({
    queryKey: ['personas'] as const,
    queryFn: () => CompanyProfilesService.listPersonas(),
    staleTime: STALE_TIME_CONFIG,
  });

// Super-admin: open (unapplied) staged data-management proposals. Drives the
// Data Changes review panel; invalidate ['data-changes'] after propose/apply/
// dismiss. Attributes search is query-driven (`['attributes', q]`) so the
// page calls `useQuery` inline with `enabled: q.length > 0` rather than a
// factory here.
export const dataChangesOptions = () =>
  queryOptions({
    queryKey: ['data-changes'] as const,
    queryFn: (): Promise<ProposedDataChange[]> =>
      DataManagementService.listChanges(),
    staleTime: STALE_TIME_DATA,
  });

// Super-admin: the DB-backed controlled lookup for a categorical attribute
// (persona department/seniority/buyer-role). Backs both the Reference Data
// management page and the persona edit form's Selects. Invalidate
// `['lookups', category]` after add/edit/delete mutations.
export const lookupValuesOptions = (category: string) =>
  queryOptions({
    queryKey: ['lookups', category] as const,
    queryFn: (): Promise<LookupValue[]> =>
      DataManagementService.listLookupValues(category),
    staleTime: STALE_TIME_CONFIG,
  });

export const dashboardDauOptions = ({
  projectId,
  dateRange,
  filterGroups,
}: GetAllEventsCount) =>
  queryOptions({
    queryKey: ['dashboard-dau', projectId, dateRange, filterGroups],
    queryFn: () =>
      DailyActiveUsersService.getDau({
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        filterGroups,
      }),
    staleTime: STALE_TIME_DATA,
  });

export const companiesOverviewOptions = ({
  projectId,
  range,
}: {
  projectId: string;
  range: number;
}) =>
  queryOptions({
    queryKey: ['companies-overview', projectId, range],
    queryFn: () =>
      GroupsService.getCompaniesOverview({ range, project_id: projectId }),
    staleTime: STALE_TIME_DATA,
  });

export const groupOverviewOptions = ({
  projectId,
  groupType,
  range,
}: {
  projectId?: string;
  groupType: string;
  range: number;
}) =>
  queryOptions({
    queryKey: ['group-overview', projectId, groupType, range],
    queryFn: () =>
      GroupsService.getGroupOverview({
        group_type: groupType,
        range,
        project_id: projectId,
      }),
    staleTime: STALE_TIME_DATA,
  });

export const companyActivityOptions = ({
  projectId,
  dateRange,
  filterGroups,
}: GetAllEventsCount) =>
  queryOptions({
    queryKey: ['company-activity', projectId, dateRange, filterGroups],
    queryFn: () =>
      CompanyActivityService.getActivity({
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        filterGroups,
      }),
    staleTime: STALE_TIME_DATA,
  });

export const eventCompaniesOptions = ({
  projectId,
  dateRange,
  filterGroups,
  eventName,
}: GetAllEventsCount & { eventName: string }) =>
  queryOptions({
    queryKey: [
      'event-companies',
      projectId,
      eventName,
      dateRange,
      filterGroups,
    ],
    queryFn: () =>
      CompanyActivityService.getForEvent({
        projectId,
        eventName,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        filterGroups,
      }),
    staleTime: STALE_TIME_DATA,
  });

type IsoRange = { startDate: string; endDate: string };

export const isoRange = (range: DateRange): IsoRange => ({
  startDate: range.startDate.toISOString(),
  endDate: range.endDate.toISOString(),
});

// Keys match the Revenue page's own queries so both share one cache entry.
export const revenueMetricsOptions = (projectId: string, range: IsoRange) =>
  queryOptions({
    queryKey: ['revenue-metrics', projectId, range],
    queryFn: () => RevenueService.getMetrics(projectId, range),
    retry: false,
    staleTime: STALE_TIME_DATA,
  });

export const revenueTimeseriesOptions = (projectId: string, range: IsoRange) =>
  queryOptions({
    queryKey: ['revenue-timeseries', projectId, range],
    queryFn: () => RevenueService.getTimeseries(projectId, range),
    retry: false,
    staleTime: STALE_TIME_DATA,
  });

export const topCompaniesOptions = ({
  projectId,
  days,
  sort,
  limit,
  filterGroups = [],
}: {
  projectId: string;
  days: number;
  sort: 'users' | 'last_seen' | 'revenue';
  limit: number;
  filterGroups?: FilterGroup[];
}) => {
  const active = filterGroups.filter((group) => group.filters.length > 0);
  return queryOptions({
    queryKey: ['admin-companies', 'top', projectId, days, sort, limit, active],
    queryFn: () =>
      GroupsService.listCompanies({
        project_id: projectId,
        range: days,
        sort,
        limit,
        filters: active.length ? JSON.stringify(active) : undefined,
      }),
    staleTime: STALE_TIME_DATA,
  });
};

export const recentSignalsOptions = (limit: number) =>
  queryOptions({
    queryKey: ['signals', 'recent', limit],
    queryFn: () => SignalsService.list({ limit }),
    staleTime: STALE_TIME_DATA,
  });
